import { useState, useCallback, useRef } from 'react'
import { Modal, btnPrimary, btnSecondary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'

interface OrgRow {
  readonly id: string
  readonly name: string
}

const PLATFORMS = ['win-x64', 'linux-x64', 'linux-arm64'] as const
const SEMVER = /^\d+\.\d+\.\d+$/

export interface UdmPackageUploadModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onUploaded: () => void
}

export function UdmPackageUploadModal({ isOpen, onClose, onUploaded }: UdmPackageUploadModalProps) {
  const [orgs, setOrgs] = useState<readonly OrgRow[]>([])
  const [orgId, setOrgId] = useState('') // '' = Global
  const [version, setVersion] = useState('')
  const [platform, setPlatform] = useState<(typeof PLATFORMS)[number]>('win-x64')
  const [releaseNotes, setReleaseNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadedRef = useRef(false)
  if (isOpen && !loadedRef.current) {
    loadedRef.current = true
    // Reset transient state from any prior open so a fresh modal doesn't show a stale
    // success banner / "Done" button or leftover file selection.
    setSuccess(null)
    setError(null)
    setVersion('')
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    void api
      .get<readonly OrgRow[]>('/organizations')
      .then((res) => setOrgs(res.data ?? []))
      .catch(() => setOrgs([]))
  }
  if (!isOpen && loadedRef.current) {
    loadedRef.current = false
  }

  const versionValid = SEMVER.test(version.trim())
  const canSubmit = versionValid && file !== null && !uploading

  const handleUpload = useCallback(async () => {
    if (!file) return
    setError(null)
    setSuccess(null)
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('version', version.trim())
      form.append('platform', platform)
      if (releaseNotes.trim()) form.append('releaseNotes', releaseNotes.trim())
      if (orgId) form.append('organizationId', orgId)
      await api.postForm('/fleet/packages', form)
      setSuccess(`Uploaded v${version.trim()} (${platform})`)
      // Reset file + version so the next platform of the same release is quick to add.
      setVersion('')
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }, [file, version, platform, releaseNotes, orgId])

  const footer: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  }

  const handleClose = () => {
    if (success) onUploaded()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload UDM Package" width="600px">
      <div>
        <label htmlFor="pkg-org" style={labelStyle}>
          Organization
        </label>
        <select
          id="pkg-org"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ ...input, width: '100%' }}
        >
          <option value="">Global (all organizations)</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <label htmlFor="pkg-version" style={labelStyle}>
          Version *
        </label>
        <input
          id="pkg-version"
          type="text"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="1.2.3"
          style={{ ...input, width: '100%' }}
        />
        {version.length > 0 && !versionValid && (
          <div style={{ fontSize: '12px', color: colors.danger, marginTop: '0.25rem' }}>Must be semver X.Y.Z</div>
        )}
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <label htmlFor="pkg-platform" style={labelStyle}>
          Platform *
        </label>
        <select
          id="pkg-platform"
          value={platform}
          onChange={(e) => setPlatform(e.target.value as (typeof PLATFORMS)[number])}
          style={{ ...input, width: '100%' }}
        >
          {PLATFORMS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <label htmlFor="pkg-notes" style={labelStyle}>
          Release notes (optional)
        </label>
        <input
          id="pkg-notes"
          type="text"
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          maxLength={5000}
          style={{ ...input, width: '100%' }}
        />
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <label htmlFor="pkg-file" style={labelStyle}>
          Installer file *
        </label>
        <input
          id="pkg-file"
          ref={fileInputRef}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ ...input, width: '100%' }}
        />
      </div>

      {success && (
        <div
          style={{
            marginTop: '0.75rem',
            padding: '0.6rem 0.75rem',
            background: colors.bgSuccess,
            border: `1px solid ${colors.borderSuccess}`,
            borderRadius: '4px',
            color: colors.success,
            fontSize: '0.875rem',
          }}
        >
          {success} — upload another platform, or click Done.
        </div>
      )}

      {error && <div style={{ color: colors.danger, fontSize: '0.875rem', marginTop: '0.75rem' }}>{error}</div>}

      <div style={footer}>
        <button type="button" style={btnSecondary} onClick={handleClose} disabled={uploading}>
          {success ? 'Done' : 'Cancel'}
        </button>
        <button type="button" style={btnPrimary} onClick={() => void handleUpload()} disabled={!canSubmit}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
    </Modal>
  )
}
